/**
 * Aplica una propuesta ya confirmada por el usuario. Qué se escribe lo decide el núcleo
 * compartido (proposalWrites, igual que en el servidor); aquí se comprueban los límites del
 * plan, se escribe todo en una sola transacción local y se devuelve cómo deshacerlo.
 */
import { checkPlanLimits, proposalWrites } from '@habitos/assistant/assistant/writes';
import { tracesFor } from '@habitos/assistant/domain/records';
import { writeMany } from '../db/repo';
import { setMeta } from '../db/db';
import { currentPlan, type Ctx } from '../domain/actions';
import { loadFocus, saveFocus, startFocus } from '../domain/focus';
import { revertWrites } from '../domain/revert';
import { uuidv7 } from '../lib/ids';
import { loadAppData, type AppData } from '../domain/useData';
import type { Proposal } from './interpreter';

export interface ApplyEnv {
  data: AppData;
  ctx: Ctx;
  /** Hay cuenta y una suscripción push activa en este dispositivo. */
  pushActive: boolean;
}

export interface ApplyResult {
  /** Clave de texto del resultado y sus valores. */
  message: string;
  params?: Record<string, string | number>;
  undo?: () => Promise<void>;
  navigate?: string;
}

export async function applyProposal(p: Proposal, env: ApplyEnv): Promise<ApplyResult> {
  // Acciones de la interfaz, sin registros de datos.
  if (p.kind === 'show_today') {
    await setMeta('todayView', 'agenda');
    return { message: 'assistant.done.today', navigate: '/' };
  }
  if (p.kind === 'start_focus') {
    if (await loadFocus()) return { message: 'assistant.done.focusBusy', navigate: '/focus' };
    await saveFocus(startFocus(p.minutes, p.habitId, p.label, Date.now()));
    return { message: 'assistant.done.focus', params: { count: p.minutes }, navigate: '/focus', undo: () => saveFocus(null) };
  }
  checkPlanLimits(p, env.data, await currentPlan());
  const planned = proposalWrites(p, { data: env.data, ctx: env.ctx, newId: () => uuidv7(), now: new Date(), pushActive: env.pushActive })!;
  const traces = tracesFor(planned.writes, env.data);
  await writeMany(planned.writes);
  return {
    message: planned.message,
    params: planned.params,
    undo: planned.writes.length ? async () => void (await revertWrites(traces)) : undefined,
  };
}

/**
 * Aplica varias propuestas como una sola acción (planificación). Si una falla, se deshacen
 * las anteriores en orden inverso para no dejar el plan a medias, y se propaga el error.
 */
export async function applyAll(items: Proposal[], env: ApplyEnv): Promise<ApplyResult> {
  if (items.length === 1) return applyProposal(items[0]!, env);
  const done: ApplyResult[] = [];
  // Cada propuesta ve los datos con lo anterior ya aplicado (por ejemplo, el orden de la lista).
  let data = env.data;
  try {
    for (const p of items) {
      done.push(await applyProposal(p, { ...env, data }));
      data = await loadAppData();
    }
  } catch (err) {
    for (const r of [...done].reverse()) await r.undo?.().catch(() => undefined);
    throw err;
  }
  const undos = done.map((r) => r.undo).filter((f): f is () => Promise<void> => Boolean(f));
  return {
    message: 'assistant.done.many',
    params: { count: items.length },
    undo: async () => {
      for (const f of [...undos].reverse()) await f();
    },
  };
}
